import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import Flag from "lucide-react/dist/esm/icons/flag";
import { api } from "../lib/api";
import { ukDate } from "../lib/format";
import { PageHeader, Card } from "../components/UI";

function FlagRow({ row, onOpen, last }) {
  const isBlind = row.flag_type === "blind";
  return (
    <tr
      onClick={() => onOpen(row)}
      data-testid={`flagged-row-${row.line_id}`}
      className={["group cursor-pointer hover:bg-surface-2/60", last ? "" : "border-b border-line-soft"].join(" ")}
    >
      <td className="px-4 py-3" style={{ width: 90 }}>
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{row.tender_ref || "—"}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{row.client_name || "—"}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Flag size={11} style={{ color: isBlind ? "#B45309" : "#0F766E", flex: "0 0 auto" }} />
          <span className="text-ink-2" style={{ fontSize: 12.5 }}>{row.description}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-ink-3 italic" style={{ fontSize: 12 }}>{row.flag_reason || "—"}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-ink-3" style={{ fontSize: 12 }}>{row.flagged_by_name || "—"}</span>
      </td>
      <td className="px-4 py-3" style={{ width: 110 }}>
        <span className="font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{row.flagged_at ? ukDate(row.flagged_at) : "—"}</span>
      </td>
      <td className="pr-4 py-3 text-right" style={{ width: 30 }}>
        <ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" />
      </td>
    </tr>
  );
}

function Section({ title, tone, rows, onOpen }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
        <span
          className="inline-flex items-center justify-center rounded-full px-2"
          style={{ fontSize: 11, fontWeight: 600, minWidth: 22, height: 20,
                   color: tone === "blind" ? "#B45309" : "#0F766E",
                   background: tone === "blind" ? "#FEF3E2" : "#D3EBE7" }}
        >
          {rows.length}
        </span>
      </div>
      <Card>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-ink-3" style={{ fontSize: 12.5 }}>Nothing flagged here.</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5">Ref</th>
                <th className="col-head px-4 py-2.5">Client</th>
                <th className="col-head px-4 py-2.5">Line item</th>
                <th className="col-head px-4 py-2.5">Reason</th>
                <th className="col-head px-4 py-2.5">Flagged by</th>
                <th className="col-head px-4 py-2.5">Flagged</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <FlagRow key={r.line_id} row={r} onOpen={onOpen} last={i === rows.length - 1} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default function FlaggedItems() {
  const navigate = useNavigate();
  const [data, setData] = useState({ blind: [], other: [], blind_count: 0, other_count: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/flagged-items")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openRow = (row) =>
    navigate(`/tenders/${row.tender_id}?tab=estimate&line=${row.line_id}`);

  return (
    <div className="app-container py-8" data-testid="flagged-items-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-ink-3 hover:text-ink inline-flex items-center gap-1 mb-3"
        style={{ fontSize: 12 }}
        data-testid="flagged-back-btn"
      >
        <ArrowLeft size={12} /> Back to dashboard
      </button>
      <PageHeader
        title="Flagged for review"
        subtitle="Line items across active tenders that need attention, oldest first."
      />
      {loading ? (
        <div className="py-16 text-center text-ink-3" style={{ fontSize: 13 }}>Loading…</div>
      ) : data.total === 0 ? (
        <Card>
          <div className="py-16 text-center">
            <p className="text-ink-2" style={{ fontSize: 13.5, fontWeight: 500 }}>Nothing flagged</p>
            <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
              Flag a line as blind or for review from the estimate to see it here.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <Section title="Assumed items" tone="blind" rows={data.blind} onOpen={openRow} />
          <Section title="Other flagged items" tone="review" rows={data.other} onOpen={openRow} />
        </>
      )}
    </div>
  );
}
